/**
 * Authentication API client methods.
 *
 * Handles registration, login, email verification, and password reset. Every
 * failure throws a RequestError (utils/apiError) that keeps the HTTP status and
 * the backend's machine-readable code, so a screen can branch on it — login's
 * email_not_verified routes to code entry instead of showing a generic failure.
 */

import { API_CONFIG } from '../config/api';
import { toRequestError } from '../utils/apiError';

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
}

export interface LoginRequest {
  identifier: string;
  password: string;
}

export interface TokenResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    username: string;
    email: string;
    email_verified: boolean;
    created_at: string;
    updated_at: string;
  };
}

export interface UserProfile {
  id: number;
  username: string;
  email: string;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * 201 body from /auth/register. The account exists but holds no token: the hard
 * gate withholds the session until /auth/verify-email redeems a code.
 */
export interface PendingVerificationResponse {
  status: 'pending_verification';
  username: string;
  email: string;
  resend_available_in_seconds: number;
}

/**
 * The deliberately uninformative 200 shared by verify-email/resend and
 * password-reset/request. Identical whether or not the address has an account,
 * so neither endpoint can be used to enumerate users.
 */
export interface GenericEmailResponse {
  status: 'ok';
  detail: string;
  resend_available_in_seconds: number;
}

export interface VerifyEmailRequest {
  email: string;
  code: string;
}

export interface PasswordResetConfirmRequest {
  email: string;
  code: string;
  new_password: string;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${API_CONFIG.baseURL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await toRequestError(response);
  }
  return response.json();
}

/**
 * Register a new account. Returns a pending-verification body — NOT a token.
 * The caller must navigate to code entry; the session is issued by verifyEmail.
 */
export async function register(data: RegisterRequest): Promise<PendingVerificationResponse> {
  return postJson<PendingVerificationResponse>('/api/v1/auth/register', data);
}

/**
 * Login. Throws RequestError with code 'email_not_verified' (status 403) when the
 * account exists but has not redeemed a signup code.
 */
export async function login(data: LoginRequest): Promise<TokenResponse> {
  return postJson<TokenResponse>('/api/v1/auth/login', data);
}

/**
 * Redeem a signup code. Returns the session that register withheld.
 */
export async function verifyEmail(data: VerifyEmailRequest): Promise<TokenResponse> {
  return postJson<TokenResponse>('/api/v1/auth/verify-email', data);
}

/**
 * Queue a fresh signup code. Generic 200 whether or not the address needs one.
 */
export async function resendVerification(email: string): Promise<GenericEmailResponse> {
  return postJson<GenericEmailResponse>('/api/v1/auth/verify-email/resend', { email });
}

/**
 * Queue a password-reset code. Generic 200 whether or not the address exists.
 */
export async function requestPasswordReset(email: string): Promise<GenericEmailResponse> {
  return postJson<GenericEmailResponse>('/api/v1/auth/password-reset/request', { email });
}

/**
 * Redeem a reset code and install a new password. Returns a fresh token, because
 * the reset bumps token_version and would otherwise kill this device's session.
 */
export async function confirmPasswordReset(
  data: PasswordResetConfirmRequest
): Promise<TokenResponse> {
  return postJson<TokenResponse>('/api/v1/auth/password-reset/confirm', data);
}

/**
 * Get current user profile.
 */
export async function getProfile(token: string): Promise<UserProfile> {
  const response = await fetch(`${API_CONFIG.baseURL}/api/v1/auth/me`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Failed to get profile');
  }

  return response.json();
}
